import { useEffect, useState } from 'react'
import { Check, ChevronDown, ChevronRight, ExternalLink, Loader2, Unlink } from 'lucide-react'
import { SiClaude, SiGooglegemini, SiOpenai } from 'react-icons/si'
import {
  AISubscriptionStatus,
  completeClaudeCodeLink,
  completeGeminiCliLink,
  getClaudeCodeStatus,
  getCodexStatus,
  getGeminiCliStatus,
  pollCodexLink,
  startClaudeCodeLink,
  startCodexLink,
  startGeminiCliLink,
  unlinkClaudeCode,
  unlinkCodex,
  unlinkGeminiCli,
} from '../lib/api'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { useAutoRefresh } from '../hooks/useAutoRefresh'

interface PasteLinkFlow {
  authUrl: string
  state: string
}

interface CodexDeviceFlow {
  verificationUrl: string
  userCode: string
  interval: number
}

/**
 * "AI Subscriptions" integration cards: Claude Code, Codex (ChatGPT), and
 * Gemini (Google). Claude and Gemini use a paste-code flow (open the provider
 * sign-in page, paste the displayed code back); Codex uses a device-code flow
 * (enter a one-time code on the provider page while we poll).
 *
 * Claude credentials are shared with the workspace terminal CLI and the chat
 * panel — connecting here signs those in too. All three are available to
 * deployed apps via the cc-app.json `credentials` array and
 * /internal/credentials.
 */
export function AISubscriptionsSection() {
  const [claudeStatus, setClaudeStatus] = useState<AISubscriptionStatus | null>(null)
  const [codexStatus, setCodexStatus] = useState<AISubscriptionStatus | null>(null)
  const [geminiStatus, setGeminiStatus] = useState<AISubscriptionStatus | null>(null)

  const [showClaude, setShowClaude] = useState(false)
  const [showCodex, setShowCodex] = useState(false)
  const [showGemini, setShowGemini] = useState(false)

  const [claudeFlow, setClaudeFlow] = useState<PasteLinkFlow | null>(null)
  const [claudeCode, setClaudeCode] = useState('')
  const [geminiFlow, setGeminiFlow] = useState<PasteLinkFlow | null>(null)
  const [geminiCode, setGeminiCode] = useState('')
  const [codexFlow, setCodexFlow] = useState<CodexDeviceFlow | null>(null)

  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    getClaudeCodeStatus().then(setClaudeStatus).catch(() => {})
    getCodexStatus().then(setCodexStatus).catch(() => {})
    getGeminiCliStatus().then(setGeminiStatus).catch(() => {})
  }, [])

  const flash = (message: string) => {
    setSuccess(message)
    setError(null)
    setTimeout(() => setSuccess(null), 3000)
  }

  const fail = (err: unknown, fallback: string) => {
    setError(err instanceof Error ? err.message : fallback)
    setSuccess(null)
  }

  // Codex device-flow polling (stops when the flow ends or the card closes)
  useAutoRefresh(
    async () => {
      if (!codexFlow) return
      try {
        const result = await pollCodexLink()
        if (!result.pending && result.linked) {
          setCodexFlow(null)
          setCodexStatus({ linked: true, service: 'codex', email: result.email, account_id: result.account_id })
          flash('Codex subscription connected')
        }
      } catch (err) {
        // Terminal poll error (expired session, provider rejection) — stop the flow.
        setCodexFlow(null)
        fail(err, 'Codex sign-in failed')
        throw err
      }
    },
    {
      baseInterval: (codexFlow?.interval || 5) * 1000,
      maxInterval: 30000,
      enabled: !!codexFlow,
    }
  )

  const startClaude = async () => {
    setBusy('claude-start')
    setError(null)
    try {
      const flow = await startClaudeCodeLink()
      setClaudeFlow({ authUrl: flow.auth_url, state: flow.state })
      setClaudeCode('')
    } catch (err) {
      fail(err, 'Failed to start Claude sign-in')
    } finally {
      setBusy(null)
    }
  }

  const confirmClaude = async () => {
    if (!claudeFlow || !claudeCode.trim()) return
    setBusy('claude-complete')
    setError(null)
    try {
      const status = await completeClaudeCodeLink(claudeCode.trim(), claudeFlow.state)
      setClaudeFlow(null)
      setClaudeCode('')
      setClaudeStatus({ ...status, linked: true })
      flash('Claude subscription connected — the terminal CLI and chat panel are signed in too')
    } catch (err) {
      fail(err, 'Failed to complete Claude sign-in')
    } finally {
      setBusy(null)
    }
  }

  const startCodex = async () => {
    setBusy('codex-start')
    setError(null)
    try {
      const flow = await startCodexLink()
      setCodexFlow({
        verificationUrl: flow.verification_url,
        userCode: flow.user_code,
        interval: flow.interval,
      })
    } catch (err) {
      fail(err, 'Failed to start Codex sign-in')
    } finally {
      setBusy(null)
    }
  }

  const startGemini = async () => {
    setBusy('gemini-start')
    setError(null)
    try {
      const flow = await startGeminiCliLink()
      setGeminiFlow({ authUrl: flow.auth_url, state: flow.state })
      setGeminiCode('')
    } catch (err) {
      fail(err, 'Failed to start Gemini sign-in')
    } finally {
      setBusy(null)
    }
  }

  const confirmGemini = async () => {
    if (!geminiFlow || !geminiCode.trim()) return
    setBusy('gemini-complete')
    setError(null)
    try {
      const status = await completeGeminiCliLink(geminiCode.trim(), geminiFlow.state)
      setGeminiFlow(null)
      setGeminiCode('')
      setGeminiStatus({ ...status, linked: true })
      flash('Gemini subscription connected')
    } catch (err) {
      fail(err, 'Failed to complete Gemini sign-in')
    } finally {
      setBusy(null)
    }
  }

  const renderCard = (opts: {
    key: string
    title: string
    icon: React.ReactNode
    summary: string
    show: boolean
    setShow: (v: boolean) => void
    status: AISubscriptionStatus | null
    connectedDetail: string
    disconnectDescription: string
    onUnlink: () => Promise<void>
    body: React.ReactNode
    footnote: string
  }) => (
    <div key={opts.key} className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => opts.setShow(!opts.show)}
        className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-secondary">{opts.icon}</div>
          <div className="text-left">
            <h3 className="font-semibold flex items-center gap-2">
              {opts.title}
              {opts.status?.linked && (
                <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                  Connected
                </span>
              )}
              {opts.status?.needs_relogin && (
                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                  Re-connect needed
                </span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground">{opts.summary}</p>
          </div>
        </div>
        {opts.show ? (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        )}
      </button>

      {opts.show && (
        <div className="p-4 pt-0 border-t border-border">
          <div className="pt-4">
            {opts.status?.linked ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Check className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-green-500">{opts.connectedDetail}</p>
                    <p className="text-sm text-muted-foreground">
                      Tokens refresh automatically; your apps receive a fresh token on every request.
                    </p>
                  </div>
                </div>

                <ConfirmDialog
                  trigger={
                    <button
                      disabled={busy === `${opts.key}-unlink`}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                    >
                      <Unlink className="w-4 h-4" />
                      {busy === `${opts.key}-unlink` ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  }
                  title={`Disconnect ${opts.title}`}
                  description={opts.disconnectDescription}
                  confirmLabel="Disconnect"
                  variant="destructive"
                  onConfirm={async () => {
                    setBusy(`${opts.key}-unlink`)
                    setError(null)
                    try {
                      await opts.onUnlink()
                      flash(`${opts.title} disconnected`)
                    } catch (err) {
                      fail(err, `Failed to disconnect ${opts.title}`)
                    } finally {
                      setBusy(null)
                    }
                  }}
                />
              </div>
            ) : (
              opts.body
            )}
          </div>

          <p className="mt-4 text-xs text-muted-foreground">{opts.footnote}</p>
        </div>
      )}
    </div>
  )

  const pasteFlowBody = (opts: {
    key: string
    providerLabel: string
    explainer: string
    flow: PasteLinkFlow | null
    code: string
    setCode: (v: string) => void
    onStart: () => void
    onConfirm: () => void
    onCancel: () => void
  }) => (
    <div className="space-y-4">
      {!opts.flow ? (
        <>
          <div className="p-4 rounded-lg bg-secondary/50">
            <p className="text-sm text-muted-foreground">{opts.explainer}</p>
          </div>
          <button
            onClick={opts.onStart}
            disabled={busy === `${opts.key}-start`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {busy === `${opts.key}-start` && <Loader2 className="w-4 h-4 animate-spin" />}
            Connect {opts.providerLabel}
          </button>
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            1. Open the sign-in page and approve access. 2. Copy the code it shows. 3. Paste it below.
          </p>
          <a
            href={opts.flow.authUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open {opts.providerLabel} sign-in
          </a>
          <div className="flex gap-2">
            <input
              type="text"
              value={opts.code}
              onChange={(e) => opts.setCode(e.target.value)}
              placeholder="Paste the code here"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={opts.onConfirm}
              disabled={!opts.code.trim() || busy === `${opts.key}-complete`}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {busy === `${opts.key}-complete` ? 'Verifying...' : 'Confirm'}
            </button>
          </div>
          <button onClick={opts.onCancel} className="text-sm text-muted-foreground hover:text-foreground">
            Cancel
          </button>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className="pt-1 border-t border-border/60">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI Subscriptions</p>
      </div>

      {(error || success) && (
        <div
          className={`p-3 rounded-lg text-sm ${
            error
              ? 'bg-destructive/10 border border-destructive/20 text-destructive'
              : 'bg-green-500/10 border border-green-500/20 text-green-500'
          }`}
        >
          {error || success}
        </div>
      )}

      {renderCard({
        key: 'claude',
        title: 'Claude Code',
        icon: <SiClaude className="w-5 h-5" />,
        summary: 'Claude subscription for the workspace terminal, chat panel, and your apps',
        show: showClaude,
        setShow: setShowClaude,
        status: claudeStatus,
        connectedDetail: claudeStatus?.subscription_type
          ? `Connected (${claudeStatus.subscription_type})`
          : 'Connected — terminal CLI and chat panel share this sign-in',
        disconnectDescription:
          'This signs you out everywhere: the workspace terminal CLI, the chat panel, and apps using the claude-code credential. You can reconnect at any time.',
        onUnlink: async () => {
          await unlinkClaudeCode()
          setClaudeStatus({ linked: false, service: 'claude-code' })
        },
        body: pasteFlowBody({
          key: 'claude',
          providerLabel: 'Claude',
          explainer:
            'Sign in with your Claude subscription (personal account). One sign-in covers the workspace terminal CLI, the chat panel, and apps that declare the claude-code credential — intended for Claude Code / Agent SDK style usage.',
          flow: claudeFlow,
          code: claudeCode,
          setCode: setClaudeCode,
          onStart: startClaude,
          onConfirm: confirmClaude,
          onCancel: () => setClaudeFlow(null),
        }),
        footnote:
          'Apps: add "claude-code" to cc-app.json credentials to receive X-Claude-Code-Token, or fetch /internal/credentials?service=claude-code. Tokens are short-lived — read them per request, never store them.',
      })}

      {renderCard({
        key: 'codex',
        title: 'Codex',
        icon: <SiOpenai className="w-5 h-5" />,
        summary: 'ChatGPT subscription for Codex-style usage in your apps',
        show: showCodex,
        setShow: setShowCodex,
        status: codexStatus,
        connectedDetail: codexStatus?.email ? `Connected as ${codexStatus.email}` : 'Connected',
        disconnectDescription:
          'Apps using the codex credential will lose access. You can reconnect at any time.',
        onUnlink: async () => {
          await unlinkCodex()
          setCodexStatus({ linked: false, service: 'codex' })
        },
        body: (
          <div className="space-y-4">
            {!codexFlow ? (
              <>
                <div className="p-4 rounded-lg bg-secondary/50">
                  <p className="text-sm text-muted-foreground">
                    Sign in with your ChatGPT account (device code). Requires device-code login to be
                    enabled in your ChatGPT security settings. The token is available to apps that
                    declare the codex credential — intended for Codex / ChatGPT-agent style usage.
                  </p>
                </div>
                <button
                  onClick={startCodex}
                  disabled={busy === 'codex-start'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {busy === 'codex-start' && <Loader2 className="w-4 h-4 animate-spin" />}
                  Connect Codex
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Open the sign-in page and enter this one-time code (expires in 15 minutes):
                </p>
                <div className="px-4 py-3 rounded-lg bg-secondary text-center font-mono text-2xl tracking-widest select-all">
                  {codexFlow.userCode}
                </div>
                <a
                  href={codexFlow.verificationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open ChatGPT sign-in
                </a>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Waiting for approval...
                </div>
                <button
                  onClick={() => setCodexFlow(null)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ),
        footnote:
          'Apps: add "codex" to cc-app.json credentials to receive X-Codex-Token and X-Codex-Account-Id, or fetch /internal/credentials?service=codex. Tokens are short-lived — read them per request, never store them.',
      })}

      {renderCard({
        key: 'gemini',
        title: 'Gemini',
        icon: <SiGooglegemini className="w-5 h-5" />,
        summary: 'Google account (Gemini Code Assist) for your apps — distinct from the Gemini API key above',
        show: showGemini,
        setShow: setShowGemini,
        status: geminiStatus,
        connectedDetail: geminiStatus?.email ? `Connected as ${geminiStatus.email}` : 'Connected',
        disconnectDescription:
          'Apps using the gemini-cli credential will lose access. You can reconnect at any time.',
        onUnlink: async () => {
          await unlinkGeminiCli()
          setGeminiStatus({ linked: false, service: 'gemini-cli' })
        },
        body: pasteFlowBody({
          key: 'gemini',
          providerLabel: 'Google',
          explainer:
            'Sign in with the Google account that carries your Gemini subscription (Code Assist entitlement). The token is available to apps that declare the gemini-cli credential — intended for Gemini-CLI / Code-Assist style usage. This is separate from the Gemini API key card.',
          flow: geminiFlow,
          code: geminiCode,
          setCode: setGeminiCode,
          onStart: startGemini,
          onConfirm: confirmGemini,
          onCancel: () => setGeminiFlow(null),
        }),
        footnote:
          'Apps: add "gemini-cli" to cc-app.json credentials to receive X-Gemini-CLI-Token, or fetch /internal/credentials?service=gemini-cli. Tokens are short-lived — read them per request, never store them.',
      })}
    </>
  )
}
